import { forwardRef, useEffect, useRef, type RefObject } from 'react';
import gsap from 'gsap';
import type { Photo } from './photos';
import type { ThemeKey } from './themes';
import { themes } from './themes';

type Props = {
  photo: Photo | null;
  visible: boolean;
  textReady: boolean;
  onBack: () => void;
  onOpenServices: () => void;
  imageBoxRef: RefObject<HTMLDivElement>;
  theme: ThemeKey;
};

const Detail = forwardRef<HTMLDivElement, Props>(function Detail(
  { photo, visible, textReady, onBack, onOpenServices, imageBoxRef, theme },
  ref,
) {
  const textRef = useRef<HTMLDivElement>(null);
  const V = themes[theme];

  useEffect(() => {
    if (!textReady || !textRef.current) return;
    const els = textRef.current.querySelectorAll<HTMLElement>('[data-anim]');
    gsap.fromTo(
      els,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.55, ease: 'power2.out', stagger: 0.07 },
    );
  }, [textReady, photo?.id]);

  useEffect(() => {
    if (!textReady && textRef.current) {
      const els = textRef.current.querySelectorAll<HTMLElement>('[data-anim]');
      gsap.set(els, { opacity: 0, y: 12 });
    }
  }, [textReady]);

  const isPortrait = photo?.orientation === 'portrait';

  const coverage = photo?.category === '창업'
    ? '사업화 · 자금'
    : photo?.category === '취업'
      ? '일자리 · 상담'
      : photo?.category === '교육'
        ? '훈련 · 교육'
        : '국비지원';

  const accent = V.bg;
  const INK = '#ffffff';
  const MUTED = 'rgba(255,255,255,0.65)';
  const BORDER = 'rgba(255,255,255,0.18)';

  return (
    <div
      ref={ref}
      className="fixed inset-0"
      style={{
        zIndex: 80,
        visibility: visible ? 'visible' : 'hidden',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.25s ease',
        background: V.radial,
      }}
      aria-hidden={!visible}
    >
      <div className="relative h-full w-full flex items-center px-10 md:px-20 gap-10 md:gap-20">
        <div
          ref={imageBoxRef}
          className="relative shrink-0"
          style={{
            width: isPortrait ? '38vw' : '52vw',
            height: isPortrait ? '78vh' : '62vh',
            maxWidth: '780px',
            borderRadius: '48px',
            overflow: 'hidden',
            background: V.itemBg,
            boxShadow: '0 40px 80px -30px rgba(0,0,0,0.35)',
          }}
        >
          {photo && (
            <img
              src={photo.src}
              alt={photo.title}
              draggable={false}
              className="w-full h-full pointer-events-none"
              style={{ objectFit: 'cover', objectPosition: 'center', display: 'block' }}
            />
          )}
        </div>

        <div ref={textRef} className="flex-1 max-w-md" style={{ color: INK }}>
          <div data-anim className="inline-flex items-center gap-3 mb-7 px-4 h-[30px] rounded-full"
            style={{ background: 'rgba(255,255,255,0.12)', border: `1px solid ${BORDER}`, backdropFilter: 'blur(8px)' }}
          >
            <span className="block w-1.5 h-1.5 rounded-full" style={{ background: '#fff' }} />
            <span className="text-[10px] tracking-[0.4em] uppercase" style={{ color: INK }}>
              {photo?.category} · {photo?.year}
            </span>
          </div>
          <h1
            data-anim
            className="font-logo-b text-3xl md:text-4xl leading-tight mb-6"
            style={{ color: INK, letterSpacing: '-0.04em' }}
          >
            {photo?.title}
          </h1>
          <p data-anim className="text-base leading-relaxed mb-8" style={{ color: INK, opacity: 0.82 }}>
            {photo?.description}
          </p>

          <div
            data-anim
            className="grid grid-cols-2 gap-3 mb-10"
          >
            <div className="rounded-3xl px-5 py-4" style={{ background: 'rgba(255,255,255,0.09)', border: `1px solid ${BORDER}` }}>
              <div className="text-[9px] tracking-[0.35em] uppercase mb-1.5" style={{ color: MUTED }}>지원 분야</div>
              <div className="text-sm" style={{ color: INK }}>{coverage}</div>
            </div>
            <div className="rounded-3xl px-5 py-4" style={{ background: 'rgba(255,255,255,0.09)', border: `1px solid ${BORDER}` }}>
              <div className="text-[9px] tracking-[0.35em] uppercase mb-1.5" style={{ color: MUTED }}>접수 방식</div>
              <div className="text-sm" style={{ color: INK }}>온라인 접수</div>
            </div>
          </div>

          {/* Commercial CTA — compact rounded pill */}
          <div data-anim className="mb-6">
            <button
              onClick={onOpenServices}
              className="group inline-flex items-center gap-3 h-[46px] pl-6 pr-5 rounded-full text-[11px] tracking-[0.32em] uppercase whitespace-nowrap cursor-pointer transition-all hover:scale-[1.02]"
              style={{ background: '#ffffff', color: accent }}
            >
              지원 분야 보기
              <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: accent, color: '#fff' }}>
                <i className="ri-arrow-right-line text-[13px]" />
              </span>
            </button>
          </div>

          {/* Back — lighter text action */}
          <div data-anim>
            <button
              onClick={onBack}
              className="group inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase whitespace-nowrap cursor-pointer transition-colors hover:opacity-100"
              style={{ color: MUTED }}
            >
              <i className="ri-arrow-left-line text-[13px] transition-transform group-hover:-translate-x-0.5" />
              갤러리로 돌아가기
            </button>
          </div>
        </div>
      </div>

      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-6 pointer-events-none">
        <div className="flex items-baseline gap-[10px] leading-none">
          <span className="font-logo-b text-[22px]" style={{ letterSpacing: '-0.03em', lineHeight: 1, color: '#fff' }}>
            다봄
          </span>
          <span className="text-[9px] tracking-[0.42em] uppercase" style={{ color: MUTED, lineHeight: 1 }}>
            상세보기
          </span>
        </div>
        <span className="text-[10px] tracking-[0.35em] uppercase" style={{ color: MUTED }}>
          {photo?.id.replace('dabom-', 'No. ')}
        </span>
      </div>
    </div>
  );
});

export default Detail;